import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import Svg, { Circle, Path } from "react-native-svg";

import type { Point } from "@jackedlogs/shared";

import type { UiTheme } from "../theme";

export type MiniChartProps = {
  points: Point[];
  // Optional second series rendered as a faded dashed line, sharing the
  // y-range with the primary so both fit in the same chart. Primary draws
  // on top.
  secondaryPoints?: Point[];
  width?: number;
  height?: number;
  yFormat?: (y: number) => string;
  theme: UiTheme;
};

// Minimal SVG line chart with dots. Designed for short series
// (≤ ~30 points) where the richer, interactive `LineChart` would be
// overkill (no time axis, no tooltip, no gap-breaking — a compact
// sparkline). The width is the SVG viewBox width, not a CSS width — the
// host View stretches it to fill the available row.
export function MiniChart({
  points,
  secondaryPoints,
  width = 320,
  height = 80,
  yFormat = (n) => n.toFixed(0),
  theme,
}: MiniChartProps) {
  const styles = makeStyles(theme);

  const computed = useMemo(() => {
    const allYs = [
      ...points.map((p) => p.y),
      ...(secondaryPoints ?? []).map((p) => p.y),
    ];
    if (allYs.length === 0) {
      return {
        primaryPath: "",
        secondaryPath: "",
        primaryDots: [] as { cx: number; cy: number }[],
        secondaryDots: [] as { cx: number; cy: number }[],
        minY: 0,
        maxY: 0,
        lastY: 0,
      };
    }
    const padX = 8;
    const padY = 12;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    let lo = Math.min(...allYs);
    let hi = Math.max(...allYs);
    if (lo === hi) {
      lo = lo - 1;
      hi = hi + 1;
    }
    const span = hi - lo;
    const toY = (y: number) => padY + (1 - (y - lo) / span) * innerH;

    const buildPath = (series: Point[]) => {
      const stepX = series.length > 1 ? innerW / (series.length - 1) : 0;
      const toX = (i: number) => padX + i * stepX;
      let d = "";
      series.forEach((p, i) => {
        const x = toX(i);
        const y = toY(p.y);
        d +=
          i === 0
            ? `M ${x.toFixed(1)} ${y.toFixed(1)}`
            : ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
      });
      const dots = series.map((p, i) => ({
        cx: toX(i),
        cy: toY(p.y),
      }));
      return { d, dots };
    };

    const primary = buildPath(points);
    const secondary = secondaryPoints
      ? buildPath(secondaryPoints)
      : { d: "", dots: [] };

    return {
      primaryPath: primary.d,
      secondaryPath: secondary.d,
      primaryDots: primary.dots,
      secondaryDots: secondary.dots,
      minY: lo,
      maxY: hi,
      lastY: points[points.length - 1]?.y ?? 0,
    };
  }, [points, secondaryPoints, width, height]);

  if (points.length === 0 && (secondaryPoints?.length ?? 0) === 0) return null;

  return (
    <View style={styles.wrap}>
      <Svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
      >
        {computed.secondaryPath ? (
          <Path
            d={computed.secondaryPath}
            fill="none"
            stroke={theme.palette.ink3}
            strokeWidth={1.25}
            strokeDasharray="3,3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {computed.secondaryDots.map((d, i) => (
          <Circle
            key={`s${i}`}
            cx={d.cx}
            cy={d.cy}
            r={2}
            fill={theme.palette.ink3}
          />
        ))}
        <Path
          d={computed.primaryPath}
          fill="none"
          stroke={theme.palette.brand500}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {computed.primaryDots.map((d, i) => (
          <Circle
            key={i}
            cx={d.cx}
            cy={d.cy}
            r={3.5}
            fill={theme.palette.brand500}
          />
        ))}
      </Svg>
      <View style={styles.meta}>
        <Text style={styles.metaMuted}>
          {`${yFormat(computed.minY)} - ${yFormat(computed.maxY)}`}
        </Text>
        <Text style={styles.metaStrong}>{yFormat(computed.lastY)}</Text>
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: t } = theme;
  return StyleSheet.create({
    wrap: { width: "100%" },
    meta: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingTop: 2,
    },
    metaMuted: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    metaStrong: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
  });
}
